import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import { callFn, fmtDate, inputStyle } from '../contracts/contractsApi';
import { BillingChip, relativeDays } from './customersUi';

const PRODUCTS = [
  { code: 'ops', label: 'Ops' },
  { code: 'workshop', label: 'Workshop', disabled: true },
];

const SORTS = {
  active: (a, b) => (b.lastActiveAt || '').localeCompare(a.lastActiveAt || ''),
  name: (a, b) => a.name.localeCompare(b.name),
  accounts: (a, b) => b.accounts - a.accounts,
  newest: (a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''),
};

export default function Customers() {
  const navigate = useNavigate();
  const [product, setProduct] = useState('ops');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('active');
  const [search, setSearch] = useState('');

  useEffect(() => {
    callFn('listOpsCustomers', {}).then(setData).catch(e => setError(e.message));
  }, []);

  const rows = (data?.customers || [])
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.orgCode || '').toLowerCase().includes(search.toLowerCase()))
    .sort(SORTS[sort]);
  const totals = data && {
    orgs: data.customers.length,
    accounts: data.customers.reduce((n, c) => n + c.accounts, 0),
    activeOrgs: data.customers.filter(c => c.activeAccounts > 0).length,
  };
  const cols = '1.6fr 110px 130px 150px 150px 110px';

  return (
    <Layout>
      <PageHeader
        title="Customers"
        subtitle={totals ? `Ops: ${totals.orgs} organisations · ${totals.accounts} accounts · ${totals.activeOrgs} active in the last ${data.activeDays} days` : error ? 'Could not load customers' : 'Loading...'}
      />
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {PRODUCTS.map(p => {
            const active = product === p.code;
            return (
              <div key={p.code} onClick={() => !p.disabled && setProduct(p.code)} title={p.disabled ? 'Coming later' : undefined} style={{
                padding: '5px 12px', borderRadius: 'var(--radius-full)', fontSize: 12,
                cursor: p.disabled ? 'not-allowed' : 'pointer', opacity: p.disabled ? 0.45 : 1,
                background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)',
                color: active ? '#fff' : 'var(--color-text-secondary)',
                border: active ? 'none' : '0.5px solid var(--color-border-light)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {p.label}
                {p.code === 'ops' && totals && <span style={{ fontSize: 10, opacity: 0.85 }}>{totals.orgs}</span>}
                {p.disabled && <span style={{ fontSize: 10 }}>later</span>}
              </div>
            );
          })}
          <input style={{ ...inputStyle, width: 220, height: 30, marginLeft: 'auto' }} placeholder="Search name or org code" value={search} onChange={e => setSearch(e.target.value)} />
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...inputStyle, width: 170, height: 30 }}>
            <option value="active">Most recently active</option>
            <option value="name">Name</option>
            <option value="accounts">Most accounts</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        {data && (
          <Card padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 16px', gap: 12, fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div>Organisation</div><div>Accounts</div><div>Active ({data.activeDays}d)</div><div>Last active</div><div>Billing</div><div>Customer since</div>
            </div>
            {rows.length === 0 && <div style={{ padding: '30px 16px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No organisations match.</div>}
            {rows.map((c, i) => (
              <div key={c.id} onClick={() => navigate(`/customers/ops/${c.id}`)}
                style={{ display: 'grid', gridTemplateColumns: cols, padding: '12px 16px', gap: 12, alignItems: 'center', fontSize: 12, cursor: 'pointer', borderBottom: i === rows.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  {c.orgCode && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>{c.orgCode}</div>}
                </div>
                <div>{c.accounts}</div>
                <div style={{ color: c.activeAccounts ? 'var(--color-success)' : 'var(--color-text-tertiary)' }}>{c.activeAccounts} of {c.accounts}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>{relativeDays(c.lastActiveAt)}</div>
                <div><BillingChip billing={c.billing} /></div>
                <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{fmtDate(c.createdAt)}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}
